import { z } from 'zod'

/**
 * Project リソースの Zod スキーマ。
 *
 * 命名規則:
 *   - <Action><Resource>InputSchema  / type <Action><Resource>Input
 *   - <Resource>Schema               / type <Resource>
 *
 * 関連: docs/system-requirements.md §4.2 / packages/db/prisma/schema.prisma の Project model
 */

export const ProjectSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: z.string().nullable(),
  folderId: z.string().uuid().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type Project = z.infer<typeof ProjectSchema>

// ----- Path / Query parameters -----

export const ProjectIdParamsSchema = z.object({
  id: z.string().uuid(),
})
export type ProjectIdParams = z.infer<typeof ProjectIdParamsSchema>

export const ListProjectsQuerySchema = z.object({
  archived: z.enum(['true', 'false']).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
})
export type ListProjectsQuery = z.infer<typeof ListProjectsQuerySchema>

// ----- Input bodies -----

export const CreateProjectInputSchema = z.object({
  name: z.string().min(1, 'name は必須です').max(200, 'name は 200 文字以内にしてください'),
  status: z.string().max(50).optional(),
  folderId: z.string().uuid().nullable().optional(),
})
export type CreateProjectInput = z.infer<typeof CreateProjectInputSchema>

export const UpdateProjectInputSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    status: z.string().max(50).nullable().optional(),
    folderId: z.string().uuid().nullable().optional(),
  })
  .refine((v) => v.name !== undefined || v.status !== undefined || v.folderId !== undefined, {
    message: 'name / status / folderId のいずれかを指定してください',
  })
export type UpdateProjectInput = z.infer<typeof UpdateProjectInputSchema>

/**
 * プロジェクト完全削除時のタイプ確認入力。
 * ユーザーには「プロジェクト名を入力してください」と要求し、入力値が一致したら削除可能とする。
 */
export const DeleteProjectInputSchema = z.object({
  confirmation: z.string().min(1, 'プロジェクト名を入力してください'),
})
export type DeleteProjectInput = z.infer<typeof DeleteProjectInputSchema>

// ----- List response (paginated) -----

export const ListProjectsResultSchema = z.object({
  items: z.array(ProjectSchema),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
})
export type ListProjectsResult = z.infer<typeof ListProjectsResultSchema>
